import React, {
  useContext, useEffect, useMemo, useState,
} from 'react'
import Table from 'components/common/Table'
import { MediatorContext } from 'context/MediatorContext'
import useQuery from 'hooks/useQuery'

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pendiente', color: 'bg-yellow-200 text-yellow-900' },
  { value: 'ongoing', label: 'En curso', color: 'bg-blue-200 text-blue-900' },
  { value: 'cancelled', label: 'Cancelada', color: 'bg-gray-200 text-gray-900' },
  { value: 'failed', label: 'Con error', color: 'bg-red-200 text-red-900' },
  { value: 'completed', label: 'Finalizada', color: 'bg-green-200 text-green-900' },
]

const STATUS_LABEL_MAP = STATUS_OPTIONS.reduce((acc, status) => {
  acc[status.value] = status
  return acc
}, {})

const formatDateTime = (value) => {
  if (!value) return '-'
  try {
    if (typeof value === 'string' && value.includes('/')) {
      // Backend ya devolvió un string formateado (DD/MM/YYYY, HH:mm:ss); lo usamos directo.
      return value
    }

    const candidates = []
    if (typeof value === 'string') {
      candidates.push(value)
      candidates.push(value.replace(' ', 'T'))
      candidates.push(
        value
          .replace(' ', 'T')
          .replace(/([+-]\d{2})(\d{2})$/, '$1:$2'), // -0300 -> -03:00, +0000 -> +00:00
      )
    } else {
      candidates.push(value)
    }

    const parsed = candidates
      .map((v) => new Date(v))
      .find((d) => !Number.isNaN(d.valueOf()))

    if (!parsed) return '-'

    return parsed.toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' })
  } catch {
    return '-'
  }
}

const StatusBadge = ({ status }) => {
  const meta = STATUS_LABEL_MAP[status] || {}
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold ${meta.color || 'bg-slate-200 text-slate-900'}`}
    >
      {meta.label || status || '—'}
    </span>
  )
}

const CallAttempts = () => {
  const { translators } = useContext(MediatorContext)
  const [selectedStatuses, setSelectedStatuses] = useState([])
  const [selectedTranslator, setSelectedTranslator] = useState('')

  const translatorOptionsFromContext = translators?.translators || []
  const { data: translatorFallback, makeRequest: fetchTranslators } = useQuery({
    isControlled: true,
    hideGlobalLoading: true,
  })

  useEffect(() => {
    if (!translatorOptionsFromContext.length && !translatorFallback?.translators) {
      fetchTranslators({
        endpoint: '/mediators/translators',
        hideGlobalLoading: true,
      })
    }
  }, [translatorOptionsFromContext.length, translatorFallback?.translators, fetchTranslators])

  const translatorOptions = translatorOptionsFromContext.length
    ? translatorOptionsFromContext
    : translatorFallback?.translators || []

  const toggleStatus = (value) => {
    setSelectedStatuses((prev) => {
      if (prev.includes(value)) {
        return prev.filter((status) => status !== value)
      }
      return [...prev, value]
    })
  }

  const clearFilters = () => {
    setSelectedStatuses([])
    setSelectedTranslator('')
  }

  const urlQueries = useMemo(() => {
    const query = {}
    if (selectedStatuses.length) query.status = selectedStatuses.join(',')
    if (selectedTranslator) query.translatorId = selectedTranslator
    return query
  }, [selectedStatuses, selectedTranslator])

  const columnsToReplace = useMemo(
    () => ({
      status: {
        Header: 'Estado',
        accessor: ({ status }) => <StatusBadge status={status} />,
      },
      reason: {
        Header: 'Motivo',
        accessor: ({ reason }) => reason || '—',
      },
      cancelledBy: {
        Header: 'Cancelado por',
        accessor: ({ cancelledBy }) => cancelledBy || '—',
      },
      errorDetail: {
        Header: 'Detalle',
        accessor: ({ errorDetail }) => errorDetail || '—',
      },
      account: {
        Header: 'Cuenta',
        accessor: ({ account }) =>
          account
            ? `${account.firstName || ''} ${account.lastName || ''} (${account.companyName || '—'})`
            : '—',
      },
      translator: {
        Header: 'Intérprete',
        accessor: ({ translator }) =>
          translator
            ? `${translator.firstName || ''} ${translator.lastName || ''}`.trim() || translator.email
            : '—',
      },
      call: {
        Header: 'Llamada asociada',
        accessor: ({ call }) =>
          call ? `${formatDateTime(call.createdAt)}${call.endedAt ? ` → ${formatDateTime(call.endedAt)}` : ''}` : '—',
      },
      createdAt: {
        Header: 'Creado',
        accessor: ({ createdAt }) => formatDateTime(createdAt),
      },
      updatedAt: {
        Header: 'Actualizado',
        accessor: ({ updatedAt }) => formatDateTime(updatedAt),
      },
    }),
    [],
  )

  return (
    <div className='flex flex-col gap-6'>
      <section className='flex flex-col gap-4 rounded bg-white p-4 shadow-sm'>
        <h2 className='text-lg font-semibold text-slate-800'>Filtros</h2>
        <div className='flex flex-wrap gap-6'>
          <div className='flex flex-col gap-2'>
            <p className='text-sm font-medium text-slate-700'>Estado</p>
            <div className='flex flex-wrap gap-3'>
              {STATUS_OPTIONS.map((status) => {
                const statusId = `status-${status.value}`
                return (
                  <label
                    key={status.value}
                    htmlFor={statusId}
                    className='flex items-center gap-2 text-sm text-slate-700 cursor-pointer select-none'
                  >
                    <input
                      id={statusId}
                      type='checkbox'
                      className='accent-[var(--color-secondary)]'
                      checked={selectedStatuses.includes(status.value)}
                      onChange={() => toggleStatus(status.value)}
                    />
                    <span>{status.label}</span>
                  </label>
                )
              })}
            </div>
          </div>
          <label
            htmlFor='translatorFilter'
            className='flex flex-col gap-2 min-w-[220px] text-sm font-medium text-slate-700'
          >
            Intérprete
            <select
              id='translatorFilter'
              className='rounded border border-slate-300 px-3 py-2 text-sm focus:border-[var(--color-secondary)] focus:outline-none'
              value={selectedTranslator}
              onChange={(e) => setSelectedTranslator(e.target.value)}
            >
              <option value=''>Todos</option>
              {translatorOptions.map((translator) => (
                <option key={translator.id} value={translator.id}>
                  {`${translator.firstName || ''} ${translator.lastName || ''}`.trim()
                    || translator.email}
                </option>
              ))}
            </select>
          </label>
          <button
            type='button'
            onClick={clearFilters}
            className='self-end rounded border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50'
          >
            Limpiar filtros
          </button>
        </div>
      </section>

      <Table
        paginated
        endpoint='/mediators/call-attempts'
        dataAccessor='attempts'
        title='Registro de llamadas'
        columnsToSkip={['metadata']}
        columnsToReplace={columnsToReplace}
        urlQueries={urlQueries}
        paginationOptions={{
          availablePageSizes: [10, 25, 50],
          initialPageSize: 25,
        }}
      />
    </div>
  )
}

export default CallAttempts
